import { createContext, useContext, useState, useMemo, ReactNode, useEffect } from "react"
import { useUser } from "@/hooks/useUser"
import { useSessionState } from "@/hooks/useSessionState"
import { buildNavPanelDefs, WorkspaceToolPanel } from "@/chat2/config/navPanels"
import { useEventListeners, EventListeners } from "@/hooks/useEventListeners"

// ============================================================================
// EVENTS
// ============================================================================

/** Events emitted by NavContext - subscribers react without triggering context updates */
export type NavEvents = {
  /** Fired when chat input focus state changes (for mobile nav hide behavior) */
  chatInputFocusChanged: (focused: boolean) => void
}

// ============================================================================
// TYPES
// ============================================================================

export type NavContextType = {
  /** All available panels */
  panels: Record<string, WorkspaceToolPanel>
  /** Currently selected panel */
  currentPanel: WorkspaceToolPanel | undefined
  /** Whether the tool panel (left sidebar content) is visible */
  displayToolPanel: boolean
  /** Event emitter/subscriber for nav events (avoids context re-renders) */
  events: EventListeners<NavEvents>

  /** Set the active panel by ID */
  setActive: (id: string, autoDisplay?: boolean) => void
  /** Show/hide tool panel */
  show: (visible: boolean | ((v: boolean) => boolean)) => void
}

// ============================================================================
// CONTEXT
// ============================================================================

const NavContext = createContext<NavContextType | null>(null)

// ============================================================================
// PROVIDER
// ============================================================================

export function NavProvider({ children }: { children: ReactNode }) {
  const [user] = useUser()
  
  // Build panel definitions from config
  const panels = useMemo(() => buildNavPanelDefs(user ?? null), [user])
  
  // Persisted state - store panel ID and visibility
  const [savedPanelId, setSavedPanelId] = useSessionState<string | null>("chat2-nav-panel-id", null)
  const [displayToolPanel, setDisplayToolPanel] = useSessionState<boolean>("chat2-nav-display", false)
  
  // Resolve current panel from saved ID
  const currentPanel = savedPanelId ? panels[savedPanelId] : undefined
  
  // Events - allows subscribers to react without triggering context re-renders
  const events = useEventListeners<NavEvents>()

  // API
  const api = useMemo<NavContextType>(() => ({
    panels,
    currentPanel,
    displayToolPanel,
    events,

    setActive: (id: string, autoDisplay?: boolean) => {
      const panel = panels[id]
      if (!panel) return
      
      setSavedPanelId(id)
      
      if (autoDisplay) {
        setDisplayToolPanel(true)
      }
    },

    show: (visible: boolean | ((v: boolean) => boolean)) => {
      setDisplayToolPanel(visible)
    },
  }), [panels, currentPanel, displayToolPanel, events, setSavedPanelId, setDisplayToolPanel])

  return (
    <NavContext.Provider value={api}>
      {children}
    </NavContext.Provider>
  )
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to access navigation panel state and methods.
 * 
 * @example
 * const nav = useNav()
 * nav.setActive("agents")
 * nav.show(true)
 * 
 * // For components that work in both old and new context systems:
 * const nav = useNav(false) // returns undefined if not in provider
 */
export function useNav(throwIfMissing?: true): NavContextType
export function useNav(throwIfMissing: false): NavContextType | undefined
export function useNav(throwIfMissing = true): NavContextType | undefined {
  const ctx = useContext(NavContext)
  if (!ctx && throwIfMissing) {
    throw new Error("useNav must be used within a NavProvider")
  }
  return ctx ?? undefined
}
